<template>
  <PosModal v-model="model" :title="$t('pos.lbl_payments')" :done-label="$t('pos.lbl_done')" :close-label="$t('pos.lbl_close')">
    <p class="text-muted small mb-2">{{ $t('pos.lbl_payments_help') }}</p>

    <!-- Lineas de pago: 1 = simple, 2+ = multiple -->
    <div v-for="(line, i) in store.payment_lines" :key="`pl-${i}`" class="pos-pay-line mb-3">
      <div class="d-flex gap-2 align-items-center">
        <select class="form-select form-select-sm" v-model="line.method">
          <option v-for="m in paymentMethods" :key="m.value" :value="m.value">{{ $t(m.label) }}</option>
        </select>

        <!-- Importe: solo en pago multiple -->
        <div class="input-group input-group-sm" v-if="store.isMultiPayment">
          <span class="input-group-text">{{ currencySymbol }}</span>
          <input type="number" min="0" step="0.01" class="form-control" placeholder="0.00" v-model="line.amount" />
        </div>

        <button v-if="store.isMultiPayment" type="button" class="btn btn-sm btn-outline-danger pos-tap" @click="store.removePaymentLine(i)" :aria-label="$t('pos.lbl_remove')">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Nota del metodo (solo en pago simple) -->
      <small class="text-muted d-block mt-1" v-if="store.isSinglePayment && methodMeta(line.method)">
        {{ methodMeta(line.method).drawer ? $t('pos.lbl_drawer_note') : methodMeta(line.method).offline ? $t('pos.lbl_offline_note') : $t('pos.lbl_reference_help') }}
      </small>

      <!-- Datafast en pago simple: widget de tarjeta (deja el resourcePath como referencia) -->
      <DatafastPayment v-if="store.isDatafastSingle && line.method === 'datafast'" />

      <!-- Referencia (medios online, salvo el widget Datafast simple) -->
      <input
        v-else-if="isOnline(line.method)"
        type="text"
        class="form-control form-control-sm mt-1"
        :placeholder="$t('pos.lbl_reference_ph')"
        v-model.trim="line.reference"
      />

      <!--
        Voucher del datafono (tarjeta con captura MANUAL). El datafono no esta
        integrado: estos datos son la unica constancia del cobro y lo que
        permite conciliar contra la liquidacion del banco. Se piden, no se
        exigen: si el cajero no los tiene a mano, mejor registrar la venta.
      -->
      <div class="mt-2" v-if="requiresVoucher(line.method)">
        <label class="form-label small mb-1">{{ $t('pos.lbl_voucher') }}</label>
        <div class="d-flex gap-2 mb-1">
          <input type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_voucher_brand')" v-model.trim="line.card_brand" />
          <input type="text" maxlength="4" class="form-control form-control-sm" :placeholder="$t('pos.lbl_voucher_last4')" v-model.trim="line.card_last4" />
        </div>
        <div class="d-flex gap-2">
          <input type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_voucher_auth')" v-model.trim="line.auth_code" />
          <input type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_voucher_batch')" v-model.trim="line.batch" />
        </div>
      </div>

      <!-- Efectivo recibido + cambio (lineas cash) -->
      <div class="mt-2" v-if="line.method === 'cash' && store.estimatedTotal > 0">
        <label class="form-label small mb-1">{{ $t('pos.lbl_cash_received') }}</label>
        <div class="input-group input-group-sm mb-1">
          <span class="input-group-text">{{ currencySymbol }}</span>
          <input type="number" min="0" step="0.01" class="form-control" placeholder="0.00" v-model="line.received" />
        </div>
        <div class="pos-cash-quick" v-if="store.isSinglePayment">
          <button type="button" class="btn btn-sm btn-outline-secondary pos-tap" v-for="a in quickAmounts" :key="`q-${a}`" @click="addCash(line, a)">+{{ money(a) }}</button>
          <button type="button" class="btn btn-sm btn-outline-primary pos-tap" @click="line.received = store.estimatedTotal">{{ $t('pos.lbl_exact') }}</button>
          <button type="button" class="btn btn-sm btn-outline-danger pos-tap" @click="line.received = null" :aria-label="$t('pos.lbl_clear')"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
        </div>
      </div>
    </div>

    <!-- Acciones: anadir medio / repartir resto -->
    <div class="d-flex gap-2 mb-3">
      <button type="button" class="btn btn-sm btn-outline-secondary pos-tap" @click="store.addPaymentLine('cash')">
        <i class="fa-solid fa-plus me-1" aria-hidden="true"></i>{{ $t('pos.lbl_add_tender') }}
      </button>
      <button v-if="store.isMultiPayment" type="button" class="btn btn-sm btn-outline-primary pos-tap" @click="store.fillLastLine()">{{ $t('pos.lbl_fill_last') }}</button>
    </div>

    <!-- Resumen -->
    <div class="pos-cash-summary">
      <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_total') }}</span><span>{{ money(store.estimatedTotal) }}</span></div>
      <template v-if="store.isMultiPayment">
        <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_split_sum') }}</span><span>{{ money(store.paymentsCovered) }}</span></div>
        <div class="d-flex justify-content-between pt-1 border-top">
          <strong>{{ store.paymentsBalanced ? $t('pos.lbl_split_balanced') : $t('pos.lbl_split_remaining') }}</strong>
          <strong :class="store.paymentsBalanced ? 'text-success' : 'text-danger'">{{ money(Math.abs(store.paymentsRemaining)) }}</strong>
        </div>
      </template>
      <div class="d-flex justify-content-between pt-1 border-top" v-if="store.changeDue > 0 || store.cashShortfall > 0">
        <strong>{{ store.cashShortfall > 0 ? $t('pos.lbl_missing') : $t('pos.lbl_change') }}</strong>
        <strong :class="store.cashShortfall > 0 ? 'text-danger' : 'text-success'">{{ money(store.cashShortfall > 0 ? store.cashShortfall : store.changeDue) }}</strong>
      </div>
    </div>
  </PosModal>
</template>

<script setup>
import { computed } from 'vue'
import PosModal from './PosModal.vue'
import DatafastPayment from './DatafastPayment.vue'
import { usePos } from '../../store/pos'
import { money } from '../money'
import { PAYMENT_METHODS } from '../constants/pos'

const props = defineProps({ modelValue: { type: Boolean, default: false } })
const emit = defineEmits(['update:modelValue'])
const model = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) })

const store = usePos()
const paymentMethods = PAYMENT_METHODS
const currencySymbol = (typeof window !== 'undefined' && window.defaultCurrencySymbol) || '$'
const quickAmounts = [5, 10, 20, 50]

const methodMeta = (m) => PAYMENT_METHODS.find((x) => x.value === m)
const isOnline = (m) => {
  const f = methodMeta(m)
  return !!f && !f.offline
}

/** Metodos de tarjeta con captura manual del voucher (PAYMENT_METHODS.voucher). */
const requiresVoucher = (method) => !!methodMeta(method)?.voucher
const addCash = (line, amount) => {
  line.received = Math.round(((Number(line.received) || 0) + amount) * 100) / 100
}
</script>
